import { getPosition, getTimes } from 'suncalc';

export const MEDICAL_LAKE_SUN_LOCATION = {
  // Target coordinate from vexcel-medical-lake-alignment.ts, not the ENU observer origin.
  latitude: 47.585570816835755,
  longitude: -117.67934695660401,
  timeZone: 'America/Los_Angeles',
};

/** Art-directed appearance driven by sun elevation, not measured irradiance.
 * Do not normalize by the day's maximum: winter noon should stay gentler. */
export function sunlightAppearance(altitude: number) {
  const elevation = Number.isFinite(altitude) ? Math.max(0, Math.min(60, altitude)) : 0;
  const t = elevation / 60;
  const strength = t * t * (3 - 2 * t);
  const warm = [244, 173, 113], daylight = [255, 250, 222];
  const channels = warm.map((v, i) => Math.round(v + (daylight[i] - v) * strength));
  const beamWarm = [247, 164, 80], beamDaylight = [255, 213, 115];
  const beamChannels = beamWarm.map((v, i) => Math.round(v + (beamDaylight[i] - v) * strength));
  return {
    color: `rgb(${channels.join(',')})`,
    beamColor: `rgb(${beamChannels.join(',')})`,
    beamOpacity: 0.2 + 0.42 * strength,
    beamEndOpacity: 0.06 + 0.14 * strength,
    glowOpacity: 0.16 + 0.2 * strength,
    glowRadius: 54 - 14 * strength,
    beamBlur: 7 - 4 * strength,
    rayOpacity: 0.04 + 0.12 * strength,
    level: elevation < 12 ? 'low' : elevation < 35 ? 'mid' : 'high',
  };
}

function localParts(instant: Date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MEDICAL_LAKE_SUN_LOCATION.timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(instant);
  const part = (name: string) => Number(parts.find(p => p.type === name)?.value);
  return { year: part('year'), month: part('month'), day: part('day'), hour: part('hour'), minute: part('minute') };
}

export function medicalLakeDate(instant = new Date()): string {
  const p = localParts(instant);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Resolve a daylight civil time in Pacific time, including the date's DST offset. */
export function medicalLakeInstant(date: string, minutes: number): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(minutes)) throw new Error('Invalid sun-path date/time');
  const [year, month, day] = date.split('-').map(Number);
  const desired = Date.UTC(year, month - 1, day, 0, minutes);
  let epoch = desired;
  for (let i = 0; i < 3; i++) {
    const p = localParts(new Date(epoch));
    const localEpoch = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    epoch += desired - localEpoch;
  }
  return new Date(epoch);
}

export function medicalLakeSunPosition(instant: Date) {
  return getPosition(instant, MEDICAL_LAKE_SUN_LOCATION.latitude, MEDICAL_LAKE_SUN_LOCATION.longitude);
}

export function medicalLakeSunDay(date: string) {
  // SunCalc 2.0 uses a solar-day window. Local noon unambiguously selects this calendar day.
  const times = getTimes(medicalLakeInstant(date, 720), MEDICAL_LAKE_SUN_LOCATION.latitude, MEDICAL_LAKE_SUN_LOCATION.longitude);
  const { sunrise, sunset } = times;
  if (!sunrise || !sunset) return null;
  const rise = localParts(sunrise), set = localParts(sunset);
  const startMinute = rise.hour * 60 + rise.minute + 1;
  const endMinute = set.hour * 60 + set.minute;
  const samples = Array.from({ length: 97 }, (_, i) =>
    medicalLakeSunPosition(new Date(sunrise.getTime() + (sunset.getTime() - sunrise.getTime()) * i / 96)));
  return { sunrise, sunset, startMinute, endMinute, samples };
}

export function sunPathTimeMarkers(date: string, day: NonNullable<ReturnType<typeof medicalLakeSunDay>>) {
  const middle = [540, 720, 900, 1080].filter(minute => minute > day.startMinute + 30 && minute < day.endMinute - 30)
    .map(minute => ({ instant: medicalLakeInstant(date, minute), kind: 'time' as const }));
  return [{ instant: day.sunrise, kind: 'sunrise' as const }, ...middle, { instant: day.sunset, kind: 'sunset' as const }];
}

export function formatMedicalLakeTime(instant: Date, locale = 'en-US', includeZone = true): string {
  return instant.toLocaleTimeString(locale, { timeZone: MEDICAL_LAKE_SUN_LOCATION.timeZone,
    hour: 'numeric', minute: '2-digit', ...(includeZone ? { timeZoneName: 'short' as const } : {}) });
}
